/**
 * The in-app half of a notification's `data.action_url`.
 *
 * `action_url` is the deep link for an alert whose subject is NOT an addressable
 * entity — a usage quota, an expiring OAuth grant, a channel connection with no
 * URL-registry entry. The backend already resolves it for the email CTA
 * (`NotificationEmailService._resolve_action_url`, shared/notifications/email_delivery.py),
 * behind a host allow-list; every in-app surface resolved the click purely from
 * `entity_type` + `entity_id` and ignored it, so such an alert's bell row, desktop
 * toast and mobile push were all inert. This is the one shared resolver the four
 * surfaces use, so they cannot drift the way three hand-copied guards would.
 *
 * **It is a security boundary, not a formatter.** The value reaches us off a
 * persisted inbox row / websocket payload and is handed straight to `navigate()`,
 * which happily leaves the app on an absolute URL — an open redirect. So this is
 * the frontend equivalent of the backend's host allow-list, and it is deliberately
 * the strictest reading: a SINGLE-slash-prefixed same-origin path, nothing else.
 * Rejected, every one returning null rather than a "cleaned" value:
 *
 * - an absolute URL (`https://evil.example.com/phish`, `javascript:…`, `mailto:…`);
 * - a protocol-relative URL (`//evil.example.com`) — `navigate('//x')` is an
 *   off-origin navigation, and it looks like a path;
 * - a backslash after the slash (`/\evil.example.com`) — browsers normalize `\`
 *   to `/` in a URL, so this IS the protocol-relative case wearing a disguise
 *   (the same parser differential the backend rejects outright);
 * - a control character anywhere (`/\tevil`, `/\n//evil.example.com`) — browsers
 *   STRIP tab/newline from a URL before parsing, so a stripped value can become
 *   protocol-relative after this check would have passed it;
 * - anything that is not a non-empty string starting with `/`.
 *
 * A rejected value is not a link, so the caller falls through to "no link" exactly
 * as it did before — never to a partially-trusted one.
 */

/** Control characters browsers strip or choke on inside a URL. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;

/**
 * The safe same-origin path in a notification's `data.action_url`, or null.
 *
 * @param data the notification's `data` bag (inbox row, websocket payload, or the
 *   flat string map an FCM push carries) — any shape; only a string value under
 *   `action_url` is eligible.
 */
export function notificationActionUrl(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  return safeNotificationPath((data as Record<string, unknown>).action_url);
}

/**
 * The predicate above, applied to a bare value rather than to a `data` bag.
 *
 * `action_url` is not the only path a notification payload can carry: a GROUPED
 * reminder's `data.group_items[].path` is the same kind of value — persisted,
 * server-supplied, handed straight to `navigate()` — so it needs the same
 * boundary. Exported so that caller reuses THIS check rather than growing the
 * fourth hand-rolled copy the docstring above warns about.
 */
export function safeNotificationPath(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (!value.startsWith('/')) return null;
  // Second character decides protocol-relative — `//host` and its `/\host` twin.
  if (value[1] === '/' || value[1] === '\\') return null;
  if (CONTROL_CHARS.test(value)) return null;
  return value;
}
